import React, { useCallback, useEffect, useState } from 'react';

import {
  fetchAnimalRetags,
  fetchRetagCandidates,
  retagAnimal,
  RETAG_REASONS,
  type AnimalRetagRecord,
  type RetagCandidate,
  type RetagReason,
} from '../data/api';

/**
 * Re-tagging: telling the system that two EIDs are one animal.
 *
 * A lost tag is the commonest reason a headcount stops adding up, and until it
 * is recorded the pen holds one animal while the data holds two -- a phantom
 * under the new tag and an unexplained disappearance under the old one. Two
 * surfaces here:
 *
 * `RetagCandidates` -- the pairs the detector found, each with the arithmetic
 * that made it a candidate, so the operator confirms a judgement rather than
 * taking one on trust.
 *
 * `RetagDialog` -- the manual path, when the operator already knows both tags.
 *
 * Neither ever links two animals without a click. The cost of welding two
 * animals together on a weight coincidence is far higher than one confirmation.
 */

const fmtDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
};

const fmtKg = (value?: number | null) => (value === null || value === undefined ? '—' : `${value.toFixed(1)} kg`);

/** Confidence as words. A bare 0.94 tells an operator nothing actionable. */
function confidenceLabel(score: number): { text: string; className: string } {
  if (score >= 0.9) return { text: 'Strong match', className: 'border-[#b9e7c9] bg-[#e7f8ee] text-[#106b38]' };
  if (score >= 0.7) return { text: 'Likely match', className: 'border-[#e0c48a] bg-[#fdf6e7] text-[#5c4300]' };
  return { text: 'Worth checking', className: 'border-[#1e6bb833] bg-[#f1f7ff] text-[#1e6bb8]' };
}

export type RetagDialogProps = {
  animalId: string;
  currentEid: string;
  /** Prefilled when confirming a suggestion. */
  suggestedEid?: string;
  fromSuggestion?: boolean;
  onDone: (retag: AnimalRetagRecord) => void;
  onCancel: () => void;
};

export function RetagDialog({
  animalId, currentEid, suggestedEid, fromSuggestion, onDone, onCancel,
}: RetagDialogProps) {
  const today = new Date().toISOString().slice(0, 10);
  const [newEid, setNewEid] = useState(suggestedEid || '');
  const [retagDate, setRetagDate] = useState(today);
  const [reason, setReason] = useState<RetagReason>('LOST');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState<AnimalRetagRecord[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetchAnimalRetags(animalId)
      .then((rows) => { if (!cancelled) setHistory(rows); })
      .catch(() => { if (!cancelled) setHistory([]); });
    return () => { cancelled = true; };
  }, [animalId]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const eid = newEid.trim();
    if (!eid || saving) return;
    setSaving(true); setError('');
    try {
      const retag = await retagAnimal(animalId, { newEid: eid, retagDate, reason, notes, fromSuggestion });
      onDone(retag);
    } catch (err: any) {
      setError(err?.message || 'Could not record the re-tag.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-[#0b223a66] px-4 py-8" onClick={onCancel}>
      <div className="max-h-full w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-avcs-text">Re-tag animal</h3>
        <p className="mt-1 text-sm text-avcs-muted">
          The animal keeps its whole history. Every weight, event and document follows the new tag, and the old
          tag keeps resolving to this animal, so a file that still carries it will not create a second record.
        </p>

        {history.length > 0 && (
          <div className="mt-3 rounded-lg border border-[#1e6bb824] bg-[#f8fbff] px-3 py-2 text-xs text-avcs-muted">
            Already re-tagged {history.length === 1 ? 'once' : `${history.length} times`}:{' '}
            {history.map((h) => `${h.oldEid} → ${h.newEid}`).join(', ')}.
          </div>
        )}

        {error && <div className="mt-3 rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]">{error}</div>}

        <form onSubmit={submit} className="mt-4 grid gap-3">
          <div className="afield">
            <span className="afield-label">Tag it carries now</span>
            <input value={currentEid} readOnly className="bg-[#f4f6f9] text-avcs-muted" />
            <span className="afield-hint">Retired when you record the re-tag.</span>
          </div>
          <div className="afield">
            <label className="afield-label" htmlFor="retag-new-eid">New EID <span className="afield-req">*</span></label>
            <input
              id="retag-new-eid"
              value={newEid}
              onChange={(e) => setNewEid(e.target.value)}
              placeholder="15-digit EID off the new tag"
              inputMode="numeric"
              autoComplete="off"
            />
            <span className="afield-hint">If a file already created a record for this tag, it is absorbed into this animal.</span>
          </div>
          <div className="afield-row md:grid-cols-2">
            <div className="afield">
              <label className="afield-label" htmlFor="retag-date">Date the new tag went in <span className="afield-req">*</span></label>
              <input id="retag-date" type="date" value={retagDate} onChange={(e) => setRetagDate(e.target.value)} />
            </div>
            <div className="afield">
              <label className="afield-label" htmlFor="retag-reason">Reason</label>
              <select id="retag-reason" value={reason} onChange={(e) => setReason(e.target.value as RetagReason)}>
                {RETAG_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          </div>
          <div className="afield">
            <label className="afield-label" htmlFor="retag-notes">Notes</label>
            <textarea
              id="retag-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything the next person needs to know"
            />
          </div>
          <div className="mt-1 flex flex-wrap justify-end gap-2">
            <button type="button" className="rounded-lg border border-[#1e6bb833] bg-white px-3 py-2 text-sm font-semibold text-avcs-text hover:bg-[#f1f7ff]" onClick={onCancel}>Cancel</button>
            <button
              type="submit"
              className="rounded-lg bg-[#1e6bb8] px-3 py-2 text-sm font-semibold text-white hover:bg-[#1a5c9f] disabled:opacity-50"
              disabled={!newEid.trim() || saving}
            >
              {saving ? 'Recording…' : 'Record re-tag'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export type RetagCandidatesProps = {
  dealId: string;
  /** Called after a suggestion is confirmed, so the caller can reload animals. */
  onRetagged?: (retag: AnimalRetagRecord) => void;
};

export default function RetagCandidates({ dealId, onRetagged }: RetagCandidatesProps) {
  const [candidates, setCandidates] = useState<RetagCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirming, setConfirming] = useState<RetagCandidate | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const check = useCallback(async () => {
    if (!dealId) return;
    setLoading(true); setError(''); setSuccess('');
    try {
      const result = await fetchRetagCandidates({ dealId });
      setCandidates(result.candidates);
      setChecked(true);
    } catch (err: any) {
      setError(err?.message || 'Could not check for re-tags.');
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { setCandidates([]); setChecked(false); setError(''); setSuccess(''); }, [dealId]);

  return (
    <div className="mb-3 rounded-xl border border-[#1e6bb824] bg-[#f8fbff] p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-avcs-text">Lost tags</p>
          <p className="mt-0.5 text-xs text-avcs-muted">
            Finds an EID that appears for the first time at a weighing, sitting where an animal that stopped
            appearing would have been by then. Nothing is linked until you confirm it.
          </p>
        </div>
        <button
          type="button"
          className="rounded-md border border-[#1e6bb824] bg-white px-2 py-1 text-xs font-semibold text-[#1e6bb8] hover:bg-[#f1f7ff] disabled:opacity-50"
          onClick={check}
          disabled={loading || !dealId}
        >
          {loading ? 'Checking…' : checked ? 'Check again' : 'Check for lost tags'}
        </button>
      </div>

      {error && <div className="mt-2 rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]">{error}</div>}
      {success && <div className="mt-2 rounded-lg border border-[#b9e7c9] bg-[#e7f8ee] px-3 py-2 text-sm text-[#106b38]">{success}</div>}

      {checked && candidates.length === 0 && !loading && !error && (
        <p className="mt-2 text-xs text-avcs-muted">
          No likely re-tags. Every animal that stopped appearing has a mortality, slaughter or dispatch event
          against it, or no newcomer's weight fits its growth.
        </p>
      )}

      {candidates.length > 0 && (
        <ul className="mt-3 grid gap-2">
          {candidates.map((candidate) => {
            const key = `${candidate.missingAnimalId}:${candidate.newAnimalId}`;
            const open = Boolean(expanded[key]);
            const confidence = confidenceLabel(candidate.score);
            return (
              <li key={key} className="rounded-lg border border-[#1e6bb824] bg-white p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
                      <span className="font-semibold tabular-nums text-avcs-text">{candidate.missingEid}</span>
                      <span className="text-avcs-muted">→</span>
                      <span className="font-semibold tabular-nums text-avcs-text">{candidate.newEid}</span>
                      <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${confidence.className}`}>{confidence.text}</span>
                    </div>
                    <p className="mt-1 text-xs text-avcs-muted">
                      Last weighed {fmtKg(candidate.lastWeight)} on {fmtDate(candidate.lastSeen)}. Turned up
                      {' '}{fmtKg(candidate.observedWeight)} on {fmtDate(candidate.newFirstSeen)}, against
                      {' '}{fmtKg(candidate.expectedWeight)} expected. Out by {fmtKg(candidate.deviationKg)}.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="rounded-md border border-[#1e6bb824] bg-white px-2 py-1 text-xs font-semibold text-avcs-text hover:bg-[#f1f7ff]"
                      onClick={() => setExpanded((prev) => ({ ...prev, [key]: !open }))}
                    >
                      {open ? 'Hide working' : 'Show working'}
                    </button>
                    <button
                      type="button"
                      className="rounded-md bg-[#1e6bb8] px-2 py-1 text-xs font-semibold text-white hover:bg-[#1a5c9f]"
                      onClick={() => setConfirming(candidate)}
                    >
                      Re-tag
                    </button>
                  </div>
                </div>
                {open && (
                  <ul className="mt-2 grid gap-1 border-t border-[#1e6bb814] pt-2">
                    {candidate.reasons.map((reason, index) => (
                      <li key={index} className="text-xs text-avcs-muted">{reason}</li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {confirming && (
        <RetagDialog
          animalId={confirming.missingAnimalId}
          currentEid={confirming.missingEid}
          suggestedEid={confirming.newEid}
          fromSuggestion
          onCancel={() => setConfirming(null)}
          onDone={(retag) => {
            setConfirming(null);
            setCandidates((prev) => prev.filter((c) => c.missingAnimalId !== retag.animalId && c.newEid !== retag.newEid));
            setSuccess(
              `${retag.oldEid} and ${retag.newEid} are now one animal.`
              + (retag.mergedAnimalId
                ? ` The duplicate record was absorbed, carrying ${retag.mergedEventCount} event(s) and ${retag.mergedWeightCount} weight record(s) across.`
                : ''),
            );
            onRetagged?.(retag);
          }}
        />
      )}
    </div>
  );
}
